import React from "react";
import { NavLink } from "react-router";
import { Box, ButtonBase, Divider, Typography, useTheme } from "@mui/material";

import { FilledChatIcon } from "../../../icons/FilledChatIcon";
import { ClaimReviewIcon } from "../../../icons/ClaimReviewIcon";
import { PreServiceReviewIcon } from "../../../icons/PreServiceReviewIcon";
import { ContractResearchIcon } from "../../../icons/ContractResearchIcon";
import ChevronRightIcon from "../../../icons/ChevronRightIcon";

interface IQuickAction {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const QUICK_ACTIONS: IQuickAction[] = [
  {
    id: "policy-questions",
    icon: <FilledChatIcon />,
    title: "Policy Questions",
    description: "Insurer policies & coverage",
  },
  {
    id: "appeal-claim",
    icon: <ClaimReviewIcon />,
    title: "Claim Review",
    description: "Challenge denials with appeals",
  },
  {
    id: "contract-research",
    icon: <ContractResearchIcon />,
    title: "Contract Research",
    description: "Payer contracts & rates",
  },
  {
    id: "pre-service-review",
    icon: <PreServiceReviewIcon />,
    title: "Pre-Service Review",
    description: "Upload scheduling documents",
  },
];

export const ExtendedHomeQuickActionsSection = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1,
        width: "100%",
        minWidth: 0,
        padding: 2,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "15px",
      }}
    >
      <Typography
        variant="h5"
        sx={{ color: "text.primary", px: 1, fontSize: "16px" }}
      >
        Quick actions
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {QUICK_ACTIONS.map((action, index) => (
          <React.Fragment key={action.id}>
            <ButtonBase
              component={NavLink}
              to={`/${action.id}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.5,
                width: "100%",
                borderRadius: "10px",
                p: 1,
                textAlign: "left",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "10px",
                  bgcolor: "custom.violetLight",
                  color: "custom.violetMain",
                  "& svg": { width: 20, height: 20 },
                }}
              >
                {action.icon}
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  {action.title}
                </Typography>
                <Typography
                  noWrap
                  variant="body2"
                  sx={{ color: "text.secondary" }}
                >
                  {action.description}
                </Typography>
              </Box>

              <ChevronRightIcon
                className="w-3 h-3 shrink-0"
                color={theme.palette.custom.iconMuted}
              />
            </ButtonBase>

            {index < QUICK_ACTIONS.length - 1 && (
              <Divider
                sx={{ mx: 1, borderColor: theme.palette.custom.cardBorder }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};
